// src/components/auth/register/SignUpConfirmation.tsx
import React, { useEffect } from "react";
import { Modal, message, Spin, Breadcrumb } from "antd";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { withFirebase, WithFirebaseProps } from "../../dev/Firebase/Firebase";
import { useUserStore } from "../../../stores/UserStore";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { HomeOutlined } from "@ant-design/icons";
import "./SignUpConfirmation.css";

const SignUpConfirmationContainer = styled.div`
  animation: fadeIn 1.5s ease-in;
  background: linear-gradient(135deg, #FFC107 0%, #FFCA28 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
  width: 100%;
  box-sizing: border-box;
  position: relative;
`;

const SignUpConfirmationCard = styled.div`
  width: 95%;
  max-width: 100%;
  margin: 0 auto;
  background: #fce248;
  box-shadow: 0 6px 20px rgba(255, 204, 0, 0.4);
  border: 1px solid #ffeb3b;
  border-radius: 12px;
  padding: 40px;
  box-sizing: border-box;
  position: relative;
  text-align: center;

  p {
    font-size: 16px;
    color: #0427f4;
    margin-top: 20px;
  }

  @media (max-width: 768px) {
    width: 96% !important;
    padding: 25px;

    p,
    .ant-typography {
      font-size: 14px !important;
    }
  }

  @media (max-width: 480px) {
    padding: 15px;

    p,
    .ant-typography {
      font-size: 12px !important;
    }
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const SignUpConfirmation: React.FC<WithFirebaseProps> = ({ firebase }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userStore = useUserStore();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");
    const oobCode = searchParams.get("oobCode");
    const continueUrl = searchParams.get("continueUrl");

    if (mode === "verifyEmail" && oobCode) {
      firebase
        .verifyEmail(oobCode)
        .then(() => {
          message.success("Email verified successfully! You can now log in.");
          userStore.setUser({ ...userStore.user, emailVerified: true });
          navigate("/login");
        })
        .catch((error: any) => {
          message.error(`Verification failed: ${error.message}`);
          navigate("/login");
        });
    } else {
      message.warning("Invalid verification link or mode.");
      navigate("/login");
    }
  }, [firebase, navigate, location.search, userStore]);

  return (
    <SignUpConfirmationContainer className="signup-confirmation-container">
      <Helmet>
        <title>Sign Up Confirmation | onelightsystem OLS - Sun Light Meditation</title>
        <meta
          name="description"
          content="Confirm your email for onelightsystem OLS to complete your registration and start your Sun Light Meditation journey with Light Civilization. Verify now to log in!"
        />
        <meta
          name="keywords"
          content="OLS sign up confirmation, email verification, Sun Light Meditation, Light Civilization, onelightsystem OLS, meditation courses, spiritual growth"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Sign Up Confirmation | onelightsystem OLS - Sun Light Meditation" />
        <meta
          property="og:description"
          content="Confirm your email for onelightsystem OLS to complete your registration and start your Sun Light Meditation journey with Light Civilization. Verify now to log in!"
        />
        <meta property="og:url" content="https://www.olsme.com/signupconfirmation" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="onelightsystem OLS Logo for Sign Up Confirmation Page" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:title" content="Sign Up Confirmation | onelightsystem OLS - Sun Light Meditation" />
        <meta
          name="twitter:description"
          content="Confirm your email for onelightsystem OLS to complete your registration and start your Sun Light Meditation journey with Light Civilization. Verify now to log in!"
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="onelightsystem OLS Logo for Sign Up Confirmation Page" />
        <link rel="canonical" href="https://www.olsme.com/signupconfirmation" />
        <link rel="preload" href="/logo.jpg" as="image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Sign Up Confirmation",
            "url": "https://www.olsme.com/signupconfirmation",
            "description": "Confirm your email for onelightsystem OLS to complete your registration and start your Sun Light Meditation journey with Light Civilization. Verify now to log in.",
            "mainEntity": {
              "@type": "Service",
              "name": "Email Verification",
              "description": "Email verification service for onelightsystem OLS users to complete registration and access Sun Light Meditation education and Light Civilization community.",
              "provider": {
                "@type": "Organization",
                "name": "onelightsystem OLS",
                "url": "https://www.olsme.com",
                "logo": "https://www.olsme.com/logo.jpg",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer service",
                  "email": "contact@olsme.com",
                  "telephone": "+1-123-456-7890", // Replace with actual number if available
                },
                "sameAs": [
                  "https://www.linkedin.com/company/onelightsystem",
                  "https://twitter.com/onelightsystem",
                  "https://www.facebook.com/onelightsystem",
                  "https://www.instagram.com/onelightsystem",
                  "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
                ],
              },
            },
            "publisher": {
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
              "logo": "https://www.olsme.com/logo.jpg",
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Sign Up Confirmation",
                  "item": "https://www.olsme.com/signupconfirmation",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Item>
            <RouterLink to="/">
              <HomeOutlined /> Home
            </RouterLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Sign Up Confirmation</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbWrapper>
      <SignUpConfirmationCard className="signup-confirmation-card">
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <Typography.Title level={1} style={{ color: "#0427f4", fontSize: "24px" }}>
            Verifying Your Email...
          </Typography.Title>
          <Text style={{ color: "#0427f4", display: "block", marginBottom: "20px" }}>
            Please wait while we verify your email address.
          </Text>
        </div>
        <Spin size="large" />
        <Text style={{ color: "#0427f4", marginTop: "20px", display: "block", textAlign: "center" }}>
          If you encounter issues,{" "}
          <RouterLink to="/contact" style={{ color: "#3552fc", textDecoration: "underline" }}>
            contact us
          </RouterLink>{" "}
          or return to{" "}
          <RouterLink to="/login" style={{ color: "#3552fc", textDecoration: "underline" }}>
            Login
          </RouterLink>
        </Text>
      </SignUpConfirmationCard>
    </SignUpConfirmationContainer>
  );
};

export default withFirebase(SignUpConfirmation);